export enum QuestionType {
  OneOfMany = 'OOMC',
  OneOfManyGraphic = 'OOMG',
  FreeText = 'FREE',
  Music = 'MUS',
  Integer = 'INT0',
  Country = 'COUN',
  Movie = 'MOV',
  Book = 'BOOK',
  PostalCode = 'POST',
  CanadianPostalCode = 'POSTCAN',
  Tactical = 'TACT',
}

/** Conditional visibility rule (Question.meta.show_if → camelized showIf). Distinct from Graphic.meta.conditional. */
export type ShowIfRule = {
  questionKey: string
  op: 'eq' | 'neq' | 'in'
  value?: string
  values?: string[]
}

/** Editorial group (stem + shared visibility via lead). Distinct from tactical meta.subQuestions. */
export type QuestionGroupMeta = {
  id: string
  stem?: { fr?: string; en?: string }
}

export type Meta = {
  columnSize?: number
  maximum?: string | number
  minimum?: string | number
  min?: string | number
  max?: string | number
  inputMask?: Record<string, string>
  answerLabels?: string[]
  graphicSuffix?: string
  infoFr?: string
  infoEn?: string
  infoUrl?: string
  sandboxFr?: string
  sandboxEn?: string
  sandboxUrl?: string
  showLabel?: boolean
  hideInSandbox?: boolean
  /** Show question only if prior answer matches (SPA navigation). */
  showIf?: ShowIfRule
  /** Shared stem + group visibility (lead owns showIf). */
  questionGroup?: QuestionGroupMeta
}

export enum DisplayType {
  Horizontal = 'horizontal_list',
  Vertical = 'vertical_list',
}

export interface GraphicMeta {
  height?: number
  width?: number
  xOffset: number
  yOffset: number
  zIndex: number
  conditional?: boolean
  preserveAspectRatio?: string
}

export interface Graphic {
  image: string
  zIndex: number
  meta?: GraphicMeta
}

export interface Choice extends OneOfManyQuestion {
  graphics?: Graphic[]
  graphicChoice?: Graphic
}

interface BaseQuestion {
  active: boolean
  staticGraphics?: Graphic[]
  defaultGraphics?: Graphic[]
  label?: string
  hint?: string
  id: string
  key: string
  optional: boolean
  order: number
  text: string
  type: QuestionType
  meta?: Meta
  notInResults?: boolean
}

export interface OneOfManyQuestion extends BaseQuestion {
  choices: Choice[]
}

export interface OneOfManyGraphicQuestion extends BaseQuestion {
  choices: Choice[]
}

export interface FreeTextQuestion extends BaseQuestion {}

export interface IntegerQuestion extends BaseQuestion {
  meta: Meta & {
    max: string
    min: string
    prefixFr: string
    prefixEn: string
    suffixFr: string
    suffixEn: string
  }
}

export interface MusicQuestion extends BaseQuestion {}

export interface CountryQuestion extends BaseQuestion {}

export interface MovieQuestion extends BaseQuestion {}

export interface BookQuestion extends BaseQuestion {}

export interface PostalCodeQuestion extends FreeTextQuestion {}

export interface CanadianPostalCodeQuestion extends FreeTextQuestion {}

export type TacticalQuestionMeta = Meta & {
  tacticalReference?: string
  subQuestions?: SubTacticalQuestion[]
}

export interface TacticalQuestion extends BaseQuestion {
  choices: Choice[]
  meta: TacticalQuestionMeta
}

export interface SubTacticalQuestion {
  id: string
  key: string
  text: string
  probability: number
}

export type Question =
  | FreeTextQuestion
  | OneOfManyQuestion
  | MusicQuestion
  | MovieQuestion
  | IntegerQuestion
  | PostalCodeQuestion
  | TacticalQuestion

export type FlatQuestion = Question & {
  category: string
}

export interface Category {
  active: boolean
  id: string
  key: string
  name: string
  order: number
  /** Several questionnaires in sequence (from API `multi_survey`). */
  multiSurvey?: boolean
  multi_survey?: boolean
  predictionModel?: string
  prediction_model?: string
  scopeCode?: string
  scope_code?: string
  urlSlug?: string
  url_slug?: string
  /** SPA theme key (AppType) when set on Category — preferred over slug heuristic. */
  appType?: string
  app_type?: string
  activeSurveys?: {
    id: string
    key: string
    order: number
    name?: string | Record<string, string>
    names?: Record<string, string>
    questionCount?: number
    question_count?: number
  }[]
  active_surveys?: {
    id: string
    key: string
    order: number
    name?: string | Record<string, string>
    names?: Record<string, string>
    questionCount?: number
    question_count?: number
  }[]
  /** Used when the payload is already normalized for the app */
  questions?: Question[]
  /** Django `question_set`, after axios `camelizeObject` */
  questionSet?: Question[]
  /** Raw JSON key if present without camelize */
  question_set?: Question[]
  /** Per-edition UI i18n overlays (flat keys Home.subTitle → string), snake or camel */
  ui_i18n_overrides?: Record<string, Record<string, string>>
  uiI18nOverrides?: Record<string, Record<string, string>>
}

/** Questions list: app-normalized `questions`, or API `question_set` / camelized `questionSet`. */
export function getCategoryQuestions(category: Category | null | undefined): Question[] {
  if (!category) {
    return []
  }
  return category.questions ?? category.questionSet ?? category.question_set ?? []
}

/** Map Django `choice_set` (camelized: `choiceSet`) onto the `choices` field the UI expects. */
export function withChoicesFromApi(question: Question): Question {
  const q = question as Question & { choiceSet?: Choice[]; choice_set?: Choice[]; choices?: Choice[] }
  const fromApi = q.choiceSet ?? q.choice_set
  if (fromApi != null) {
    // Prefer API list when `choices` is missing or an empty placeholder array.
    const existing = q.choices
    const choices = Array.isArray(existing) && existing.length > 0 ? existing : fromApi
    return { ...q, choices } as Question
  }
  return question
}

/** Choices attached to a question (API `choice_set` or normalized `choices`). */
export function getQuestionChoices(question: Question): Choice[] {
  const normalized = withChoicesFromApi(question) as Question & { choices?: Choice[] }
  return normalized.choices ?? []
}

/**
 * FREE questions with a non-empty choice list behave like one-of-many in the UI
 * (vertical list + portrait graphics per choice).
 */
export function questionHasChoiceList(question: Question): boolean {
  return getQuestionChoices(question).length > 0
}

export interface Survey {
  active: boolean
  category: Category | null
  id: string
  key: string
  name: string
  order: number
  graphics: Graphic[]
  version: number
}

export interface ProbabilityResults {
  visitorId: string
}
